// src/context/RegistrationContext.js
import { httpsCallable } from 'firebase/functions';
import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { functions } from '../firebase';
import { useParticipantData } from '../hooks/useParticipantData';
import {
  findOrCreateNewParticipant,
  findParticipantById,
  updateParticipantRegistration
} from '../services/RegistrationService';
import { clearCookies, getCookie, setCookie } from '../utils/cookieUtils';
import { useAlert } from './AlertContext';

// Create the registration context
const RegistrationContext = createContext();

/**
 * Custom hook to access the registration context
 * @returns {Object} Registration context value
 */
export const useRegistration = () => useContext(RegistrationContext);

/**
 * Provider component for registration functionality
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const RegistrationProvider = ({ children }) => {
  // Extract URL parameters
  const { projectId: urlProjectId, pId: urlPId, code: urlCode } = useParams();

  // Core state variables
  const [projectId] = useState(urlProjectId || getCookie('projectId') || null);
  const [pid, setPid] = useState(urlPId?.length > 20 ? urlPId : null);
  const [participantId, setParticipantId] = useState(null);
  const [participant, setParticipant] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');

  // Hooks
  const { addAlert } = useAlert();
  const navigate = useNavigate();
  const [fetchedParticipant, loadingParticipant] = useParticipantData(participantId);

  /**
   * Centralized error handling function
   * @param {Error|string|null} err - Error object or message
   * @param {string} userMessage - User-friendly error message
   */
  const handleError = useCallback((err = null, userMessage) => {
    if (err) {
      console.error(err);
    }
    addAlert('error', userMessage);
    setLoading(false);
  }, [addAlert]);

  // Effect to update participant data when fetched
  useEffect(() => {
    setLoading(true);
    if (fetchedParticipant) {
      setParticipant(fetchedParticipant);
      // Ensure active step is always stored as a number
      const stepVal = Number(fetchedParticipant.registrationStep ?? 0);
      setActiveStep(Number.isNaN(stepVal) ? 0 : stepVal);
      if (fetchedParticipant.registrationType === 'email' || fetchedParticipant.registrationType === 'direct') {
        setEmail(fetchedParticipant.registration?.email?.address || '');
      }
    }
    setLoading(false);
  }, [fetchedParticipant, loadingParticipant, participantId, handleError]);

  // Effect to load data from cookies on mount
  useEffect(() => {
    const retrieveParticipantCookies = () => {
      setLoading(true);
      try {
        const cookiePid = getCookie('pid');
        const cookieParticipantId = getCookie('participantId');
        if (cookiePid)
          setPid(cookiePid);
        if (cookieParticipantId)
          setParticipantId(cookieParticipantId);
      } catch (err) {
        handleError(err, 'Error loading participant data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    const setParticipantIdFromCode = () => {
      if (urlCode !== null && urlCode !== undefined) {
        setParticipantId(urlCode);
        setCookie('participantId', urlCode, 7);
      }
    };

    retrieveParticipantCookies();
    setParticipantIdFromCode();
  }, [handleError, urlCode]);

  // Effect to sync active step with participant data
  useEffect(() => {
    if (participant?.registrationStep && participant.registrationStep !== activeStep) {
      console.log("RegistrationProvider:useEffect:participant:activeStep", activeStep, participant?.registrationStep);
      setLoading(true);
      //setActiveStep(participant.registrationStep);
      console.log("RegistrationProvider:useEffect:participant:registrationStep", participant?.registrationStep);
      setLoading(false);
    }
  }, [activeStep, participant]);

  /**
   * Stores Prolific ID and creates/finds participant
   * @returns {Promise<void>}
   */
  const storePid = async () => {
    setLoading(true);
    if (!pid || pid.length < 23) {
      handleError(null, 'Invalid Prolific ID, Please enter your Prolific ID');
      return;
    }

    try {
      const newParticipantId = await findOrCreateNewParticipant(pid, projectId, 'prolific');
      const participantData = await findParticipantById(newParticipantId);

      setParticipantId(newParticipantId);
      setCookie('pid', pid, 7);
      setCookie('participantId', newParticipantId);

      if (participantData) {
        setParticipant(participantData);
        setActiveStep(participantData.registrationStep);
      }

      addAlert('success', 'Successfully stored Prolific ID');
    } catch (err) {
      handleError(err, 'Error registering your Prolific ID. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Registers a participant with email
   * @returns {Promise<void>}
   */
  const storeEmail = useCallback(async () => {
    setLoading(true);
    try {
      // The server creates (or finds) the participant and emails a code.
      // The code never reaches the browser.
      const startEmailRegistration = httpsCallable(functions, 'startEmailRegistration');
      const result = await startEmailRegistration({ email, projectId });
      const newParticipantId = result?.data?.participantId;
      if (!newParticipantId) throw new Error('Registration could not be started.');

      setParticipantId(newParticipantId);
      setCookie('participantId', newParticipantId, 7);
      setCookie('email', email, 7);

      setActiveStep(1);
      addAlert('success', 'Verification email sent. Please check your inbox.');
    } catch (error) {
      handleError(error, "Failed to register with email. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [email, projectId, handleError, addAlert]);

  /**
   * Verifies email with verification code
   * @param {string} verificationCode - Code to verify
   * @returns {Promise<void>}
   */
  const verifyEmail = useCallback(async (verificationCode) => {
    setLoading(true);
    try {
      if (!participantId) {
        throw new Error("No participant ID found. Please restart registration.");
      }
      // Checked on the server; the browser never sees the stored code.
      const verifyEmailRegistration = httpsCallable(functions, 'verifyEmailRegistration');
      const result = await verifyEmailRegistration({
        participantId,
        code: String(verificationCode || '').trim(),
      });
      if (result?.data?.participant) {
        setParticipant(result.data.participant);
      }
      setActiveStep(2);
      addAlert('success', 'Email verified successfully.');
    } catch (error) {
      handleError(error, "Failed to verify email: " + error.message);
    } finally {
      setLoading(false);
    }
  }, [participantId, handleError, addAlert]);

  /**
   * Initiates Fitbit authorization flow
   * @returns {Promise<void>}
   */
  const initiateFitbitAuth = async () => {
    setLoading(true);
    try {
      setCookie('projectId', projectId);
      if (pid) {
        setCookie('pid', pid);
      }
      setCookie('participantId', participantId);

      // Google Health consent (replaces the legacy Fitbit Web API, shut down Oct 30, 2026).
      const startHealthAuthorization = httpsCallable(functions, 'startHealthAuthorization');
      const result = await startHealthAuthorization({ participantId });

      if (result?.data?.redirectUri) {
        window.location.href = result.data.redirectUri;
      } else {
        handleError(null, 'Error initiating Fitbit authorization. Please try again.');
      }
    } catch (err) {
      handleError(err, 'Error initiating Fitbit authorization. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Updates participant registration step
   * @param {number} step - New registration step
   * @param {Object} additionalData - Additional data to update
   * @returns {Promise<void>}
   */
  const updateRegistrationStep = useCallback(async (step, additionalData = {}) => {
    setLoading(true);
    try {
      const updatedParticipant = await updateParticipantRegistration(
        participantId, step, additionalData
      );

      if (updatedParticipant) {
        setParticipant(updatedParticipant);
        const stepNum = Number(step);
        setActiveStep(Number.isNaN(stepNum) ? 0 : stepNum);
      }
    } catch (err) {
      handleError(err, 'Error updating registration step. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [participantId, handleError]);

  /**
   * Resets the registration process
   * @returns {Promise<void>}
   */
  const renewRegistration = async () => {
    setLoading(true);
    try {
      clearCookies(['pid', 'participantId', 'email']);
      setActiveStep(0);
      setPid(null);
      setParticipantId(null);
      setParticipant(null);
      setEmail('');

      addAlert('success', 'Registration process has been reset.');
      navigate(`/reg/prolificRegistration/${projectId}`, {replace: true});
    } catch (err) {
      handleError(err, 'Error renewing registration process. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const runCronJobOnDate = useCallback(async (downloadDate) => {
    setLoading(true);
    try {
      // Normalize/validate date -> YYYY-MM-DD
      let formattedDate = '';
      if (downloadDate instanceof Date) {
        formattedDate = downloadDate.toISOString().split('T')[0];
      } else if (typeof downloadDate === 'string' && downloadDate.length === 10) {
        formattedDate = downloadDate;
      } else {
        throw new Error('Invalid date format. Expected YYYY-MM-DD.');
      }

      // "Email me my data": the server sends the day's data to this participant's verified
      // address (at most once a day); it never takes an address from the browser.
      const requestHealthDataEmail = httpsCallable(functions, 'requestHealthDataEmail');
      await requestHealthDataEmail({ participantId, date: formattedDate });

      console.log('Data download request accepted');

      // Advance to the next logical step based on current participant/activeStep
      try {
        const currentStep = Number(participant?.registrationStep ?? activeStep ?? 0);
        const nextStep = Math.min(currentStep + 1, 5);
        await updateRegistrationStep(nextStep);
      } catch (e) {
        // Non-fatal; still report success to caller
        console.warn('Failed to advance step to 4:', e);
      }

      return true;
    } catch (err) {
      // The server's messages for expected refusals are written for participants.
      const known = ['once-a-day', 'no-verified-email', 'not-connected', 'withdrawn'];
      handleError(err, known.includes(err?.details?.key) ? err.message : 'Error requesting your data. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [participantId, participant, activeStep, handleError, updateRegistrationStep]);

  /**
   * Generates registration URL based on current state
   * @returns {string|null} Registration URL
   */
  const getRegistrationUrl = () => {
    const cookieProjectId = getCookie('projectId');
    const cookiePid = getCookie('pid');
    const cookieParticipantId = getCookie('participantId');

    if ((!projectId && !cookieProjectId)) {
      return null;
    }

    const projId = cookieProjectId || projectId;
    const pId = cookiePid || pid;
    const localParticipantId = participantId || cookieParticipantId;
    if (localParticipantId) {
      const regUrl = findParticipantById(localParticipantId).then((participantData) => {
        if (participantData) {
          if (participantData.registrationType === 'direct') {
            return `/reg/emailRegistrationDirect/${projId}/code/${localParticipantId}`;
          } else if (participantData.registrationType === 'email') {
            return `/reg/emailRegistration/${projId}/code/${localParticipantId}`;
          } else {
            return `/reg/prolificRegistration/${projId}${pId ? '/pId/' + pId : ''}`;
          }
        } else {
          console.error("No such participant document!");
          return `/reg/prolificRegistration/${projId}${pId ? '/pId/' + pId : ''}`;
        }
      }).catch((error) => {
        console.error("Error getting participant document:", error);
        return `/reg/prolificRegistration/${projId}${pId ? '/pId/'
          + pId : ''}`;
      });
      return regUrl;
    } else {
      if (projId && pId) {
        return `/reg/prolificRegistration/${projId}/pId/${pId}`;
      } else if (projId) {
        return `/reg/prolificRegistration/${projId}`;
      } else {
        return null;
      }
    }
  }

  // Context value object
  const contextValue = {
    pid,
    setPid,
    projectId,
    participantId,
    participant,
    setParticipant,
    activeStep,
    loading,
    setLoading,
    storePid,
    initiateFitbitAuth,
    updateRegistrationStep,
    renewRegistration,
    getRegistrationUrl,
    handleError,
    email,
    setEmail,
    storeEmail,
    verifyEmail,
    runCronJobOnDate
  };

  return (
    <RegistrationContext.Provider value={contextValue}>
      {children}
    </RegistrationContext.Provider>
  );
};

// PropTypes definition
RegistrationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
